import {
  Box,
  Divider,
  Grid,
  useTheme,
  Fade,
  Grow,
  Slide,
  Skeleton,
} from '@mui/material';
import OptionCustomTableAllGreeks from 'app/components/OptionChain/OptionCustomTableAllGreeks';
import { PositionWithExpiryData } from 'app/components/Simulator/OptionChain/types';
// import { calculatePOPForMultiplePositions } from 'app/components/Simulator/PayoffChart/utils';
import CircularLoader from 'app/design/uiComponents/CircularLoader';
import moment from 'moment';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import useGetOptionChainMetadata from 'services/Simulator/useGetOptionChainMetadata';
import useGetOptionChainData, {
  useGetOptionChainExpiredED,
  useGetOptionChainExpiryData,
} from 'services/Simulator/useGetOptionChianData';
import {
  FutureDataType,
  SymbolOptionChainDataType,
} from 'types/ApiServicesTypes';
import {
  expiredDateSquarOffPosition,
  getExpiredPositions,
} from 'app/components/Simulator/OptionChain/utils';
import PageHeader from 'app/design/speedBot/PageHeader';
import { isEqual } from 'lodash';
import MuiChip from 'app/design/uiComponents/MuiChip';
import { PositionBaseDataType, PositionData } from 'app/components/Simulator/OptionChain/types';
import TimeLineContent from 'app/components/Simulator/TimeLine';

export type OptionTabMetaData = {
  symbol: string;
  optionExpiry: string;
  future: string;
};

export type OptionChainWithExpiryType = Record<
  string,
  {
    option: Record<string, SymbolOptionChainDataType>;
    future: Record<string, FutureDataType>;
  }
>;

const getDefaultDate = () => {
  const today = moment();
  let prev = today.clone().subtract(1, 'days');
  // If previous day is Sunday (0) or Saturday (6), go back to Friday
  if (prev.day() === 0) { // Sunday
    prev = prev.subtract(2, 'days');
  } else if (prev.day() === 6) { // Saturday
    prev = prev.subtract(1, 'days');
  }
  return prev.format('YYYY-MM-DD 09:16:00');
};

const OptionChain = () => {
  const [date, setDate] = useState<string>(getDefaultDate());
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const { data: queryData, isLoading: isMetadataLoading } =
    useGetOptionChainMetadata(moment.utc(date).format('YYYY-MM-DD').toString());

  const metaData = queryData?.data;
  const indexes = (metaData && Object.keys(metaData.lot_sizes)) || ['NIFTY 50'];
  const [optionTabMetaData, setOptionTabMetaData] = useState<OptionTabMetaData>(
    {
      symbol: indexes[0],
      optionExpiry: (metaData && metaData.options[indexes[0]][0]) || '',
      future: (metaData && metaData.futures[indexes[0]][0]) || '',
    },
  );
  const optionExpiry =
    metaData?.options[optionTabMetaData.symbol || indexes[0]];
  const futureExpiry =
    metaData?.futures[optionTabMetaData.symbol || indexes[0]];
  const [positions, setPositions] = useState<PositionWithExpiryData>({});
  
  const optionchainWithExpiryData = useGetOptionChainExpiryData(
    optionTabMetaData.symbol,
    [...Object.keys(positions), optionTabMetaData.optionExpiry],
    moment(date, 'YYYYMMDD HH:mm')
      .utcOffset(330)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss')
      .toString(),
  );

  const { data: optionChainStrikeData, isLoading: isOptionChainLoading } =
    useGetOptionChainData(
      optionTabMetaData.symbol,
      optionTabMetaData.optionExpiry,
      moment(date, 'YYYYMMDD HH:mm')
        .utcOffset(330)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss')
        .toString(),
    );
  const [optionChainDatawithExpiry, setOptionChainDatawithExpiry] =
    useState<OptionChainWithExpiryType>({
      [`${optionTabMetaData.optionExpiry}`]: {
        option: optionChainStrikeData
          ? optionChainStrikeData.candles
          : ({} as Record<string, SymbolOptionChainDataType>),
        future: optionChainStrikeData
          ? optionChainStrikeData.futures
          : ({} as Record<string, FutureDataType>),
      },
    });

  const [optionChianData, setOptionChainData] = useState({
    [optionTabMetaData.optionExpiry]: optionChainStrikeData,
  });


  useEffect(() => {
    setOptionTabMetaData({
      symbol: optionTabMetaData.symbol,
      optionExpiry: optionExpiry ? optionExpiry[0] : '',
      future: futureExpiry ? futureExpiry[0] : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionExpiry]);

  // Update expiry when symbol changes
  useEffect(() => {
    if (metaData && optionTabMetaData.symbol && metaData.options[optionTabMetaData.symbol]) {
      const symbolExpiries = metaData.options[optionTabMetaData.symbol];
      if (symbolExpiries.length > 0 && !symbolExpiries.includes(optionTabMetaData.optionExpiry)) {
        setOptionTabMetaData(prev => ({
          ...prev,
          optionExpiry: symbolExpiries[0]
        }));
      }
    }
  }, [optionTabMetaData.symbol, metaData, optionTabMetaData.optionExpiry]);
  useEffect(() => {
    setOptionChainDatawithExpiry({
      ...optionChainDatawithExpiry,
      [`${optionTabMetaData.optionExpiry}`]: {
        option: optionChainStrikeData
          ? optionChainStrikeData.candles
          : ({} as Record<string, SymbolOptionChainDataType>),
        future: optionChainStrikeData
          ? optionChainStrikeData.futures
          : ({} as Record<string, FutureDataType>),
      },
    });
    setOptionChainData({
      ...optionChianData,
      [optionTabMetaData.optionExpiry]:
        optionChainStrikeData === undefined
          ? Object.values(optionChianData)[
              Object.values(optionChianData).length - 1
            ]
          : optionChainStrikeData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, optionChainStrikeData]);

  useEffect(() => {
    // Trigger page entrance animation
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const theme = useTheme();

  // Set CSS custom properties for theme colors
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.palette.primary.main);
    root.style.setProperty('--skeleton-color-1', theme.palette.grey[100]);
    root.style.setProperty('--skeleton-color-2', theme.palette.grey[200]);
    root.style.setProperty(
      '--skeleton-shimmer-color',
      `rgba(${theme.palette.primary.main}, 0.1)`,
    );
    root.style.setProperty('--timeline-hover-shadow', theme.shadows[4]);
    root.style.setProperty('--card-hover-shadow', theme.shadows[12]);
  }, [theme]);

  // 1. State to hold the displayed data
  const [displayedOptionChainWithExpiryData, setDisplayedOptionChainWithExpiryData] = useState(optionchainWithExpiryData);
  const prevDataRef = useRef(optionchainWithExpiryData);

  // 2. Effect to update only when new data is available
  useEffect(() => {
    if (
      optionchainWithExpiryData && Object.keys(optionchainWithExpiryData) && Object.keys(optionchainWithExpiryData).length > 0 &&
      !isEqual(optionchainWithExpiryData, prevDataRef.current)
    ) {
      setDisplayedOptionChainWithExpiryData(optionchainWithExpiryData);
      prevDataRef.current = optionchainWithExpiryData;
    }
  }, [optionchainWithExpiryData]);

  // Handler for option clicks
  const handleOptionClick = useCallback(
    (position: PositionBaseDataType, contractType: string, strike: string) => {
      // Handle option click logic here
      // console.log('Option clicked:', position, contractType, strike);
    },
    []
  );

  // Get current strike price (you might need to adjust this based on your data structure)
  const currentStrike = optionChainStrikeData?.spot_price || 0;

  // Get current expiry positions
  const currentExpiryPositions = positions[optionTabMetaData.optionExpiry] || {};

  // Get option chain data for current expiry
  const currentExpiryOptionChainData = displayedOptionChainWithExpiryData[optionTabMetaData.optionExpiry]?.option || {};

  return (
    <Fade in={isPageLoaded} timeout={800}>
      <Box>
        <CircularLoader open={false} zIndex={1302} />
        <Helmet>
          <title>Option Chain </title>
          <meta name="description" content="option chain Page" />
        </Helmet>
        <Slide direction="down" in={isPageLoaded} timeout={600}>
          <Box
            className="sticky-top"
            sx={{
              backgroundColor: theme.palette.common.white,
              transition: theme.transitions.create(['transform', 'opacity'], {
                duration: theme.transitions.duration.standard,
              }),
            }}
          >

            {/* <PageHeader variant={''} title={'Simulator'} /> */}
            <PageHeader
          variant="market"
          title={
            <>
              Option chain
              <MuiChip
                  label={'Beta'}
                  size="medium"
                  color={'greylight'}
                  sx={{ m: 1 }}
                />
            </>
          }
        />
            <Divider sx={{ borderBottom: 2, borderColor: 'grey.300' }} />
          </Box>
        </Slide>
        
        {/* Timeline for date selection */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <TimeLineContent 
            date={date} 
            setDate={setDate} 
            isOnlyOptionChain={true}
            symbol={optionTabMetaData.symbol}
            setSymbol={(symbol) => setOptionTabMetaData(prev => ({ ...prev, symbol }))}
            expiry={optionTabMetaData.optionExpiry}
            setExpiry={(expiry) => setOptionTabMetaData(prev => ({ ...prev, optionExpiry: expiry }))}
            symbols={indexes}
            expiries={optionExpiry || []}
            spotPrice={currentStrike}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent:'center'}}>
        <Box
          className=""
          sx={{
            height:'80vh',
            width: '100vw',
            overflow: 'hidden',
          }}
        >
          <OptionCustomTableAllGreeks
            optionChainData={currentExpiryOptionChainData}
            handleOptionClick={handleOptionClick}
            currentStrike={currentStrike}
            positions={currentExpiryPositions}
            isCE={true}
            date={date}
          />
        </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default OptionChain;
